/**
 * MODULE: _shared/frontend-shell/src/hooks/assistant/assistantStreamContextQueryType.ts
 * PURPOSE: Map orchestration SSE complete metadata → POST /assistant/context/resolve query_type.
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-07-08 [2.2.1484] — Brain rail scopes after action-first / brief / pipeline streams.
 */

/** Mirrors backend ``TOPIC_SLUG_SCOPE_ALIASES`` (context_scoper.py) for dock/page context resolve. */
const TOPIC_SLUG_CONTEXT_QUERY_TYPE: Record<string, string> = {
  'research-account': 'company_research',
  'research-person': 'company_research',
  'build-account-brief': 'company_research',
  'enrich-contact': 'crud_read',
  'draft-outreach': 'messaging',
  'draft-follow-up': 'messaging',
  'stage-lookup': 'crud_read',
  'schedule-meeting': 'crud_create',
  'log-activity': 'crud_update',
  'create-task': 'crud_create',
  'update-opportunity': 'crud_update',
  'team-messaging': 'messaging',
  'manage-canvases': 'widget',
  'manage-widgets': 'widget',
  'sales-hub-content': 'general_chat',
  'pipeline-by-stage': 'analyze_pipeline',
  'top-accounts': 'analyze_pipeline',
  'top-reps': 'get_leaderboard',
  'forecast-summary': 'get_forecast',
  'export-report': 'report',
  assistant_platform_tools: 'general_chat',
  create_contact: 'crud_create',
  'align-team-brief': 'crud_read',
  'align-team-rollup': 'crud_read',
  'align-team-propose': 'crud_create',
  'align-team-block': 'crud_update',
  'align-team-assign-role': 'crud_update',
  champion_internal_sell: 'champion_internal_sell',
  reengage_quiet_champions: 'reengage_quiet_champions',
  quiet_champions: 'quiet_champions',
  company_research: 'company_research',
};

/** Orchestration ``complete.metadata.query_type`` → context resolve intent. */
const STREAM_QUERY_TYPE_CONTEXT: Record<string, string> = {
  assistant_capability: 'general_chat',
  assistant_recommend: 'general_chat',
  assistant_pipe_gen: 'general_chat',
  assistant_sop_coaching: 'crud_read',
  assistant_playbook_next_step: 'crud_read',
  assistant_pipeline_summary: 'analyze_pipeline',
  assistant_activity_feed: 'crud_read',
  assistant_follow_up_execute: 'crud_create',
  assistant_action_first: 'general_chat',
  company_research: 'company_research',
  analyze_pipeline: 'analyze_pipeline',
  crud_create_contact: 'crud_create',
  create_contact: 'crud_create',
  assistant_platform_tools: 'general_chat',
  segment_account_patch: 'crud_update',
};

export function resolveContextQueryTypeFromStreamMetadata(
  metadata: unknown,
): string | undefined {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return undefined;
  }
  const m = metadata as Record<string, unknown>;
  const queryType = typeof m.query_type === 'string' ? m.query_type.trim() : '';
  const topicSlug =
    (typeof m.topic_slug === 'string' ? m.topic_slug : '') ||
    (typeof m.agent_matched_topic_slug === 'string' ? m.agent_matched_topic_slug : '');
  const slug = topicSlug.trim();
  if (slug) {
    const aliased = TOPIC_SLUG_CONTEXT_QUERY_TYPE[slug];
    if (aliased) return aliased;
  }
  if (queryType) {
    const mapped = STREAM_QUERY_TYPE_CONTEXT[queryType];
    if (mapped) return mapped;
    if (queryType !== 'general_chat') return queryType;
  }
  if (slug) return slug;
  return undefined;
}
